import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, X, Send, Bot, User as UserIcon } from 'lucide-react';
import axios from 'axios';
import { brand } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getSessionId() {
  let sid = localStorage.getItem('ec_chat_session');
  if (!sid) {
    sid = `web_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem('ec_chat_session', sid);
  }
  return sid;
}

const QUICK_QUESTIONS = [
  'Which marketplaces do you cover?',
  'How much does account management cost?',
  'Can you build a website for my brand?',
  'I want to sell on Amazon. Where do I start?',
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hi! I'm the EC Safetrade AI assistant. Ask me about our marketplace services, pricing, or how we can help grow your business.`,
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: getSessionId(),
        message: msg,
      }, { timeout: 45000 });
      const reply = res.data?.reply || 'Sorry, I could not process that.';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `I'm having trouble connecting right now. You can WhatsApp us directly at ${brand.phone} for a fast response.`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toggle button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed z-[60] right-5 bottom-40 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-2xl hover:scale-110 transition-transform"
          style={{
            background: 'linear-gradient(135deg, #1F8B4C 0%, #146638 100%)',
            boxShadow: '0 12px 28px -6px rgba(31,139,76,0.55)',
          }}
          aria-label="Open chat"
        >
          <MessageSquare size={24} />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-[color:var(--brand-orange)] rounded-full ring-2 ring-white animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-[60] right-5 bottom-5 w-[92vw] max-w-[400px] h-[560px] max-h-[80vh] bg-white rounded-3xl shadow-2xl border border-neutral-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div
            className="px-5 py-4 flex items-center justify-between text-white"
            style={{ background: 'linear-gradient(135deg, #1F8B4C 0%, #146638 100%)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot size={20} />
              </div>
              <div>
                <div className="font-bold">EC Safetrade Assistant</div>
                <div className="text-[11px] text-white/85 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#7FE0A0] animate-pulse" />
                  Online now
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/85 hover:text-white">
              <X size={22} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[color:var(--brand-green-light)]/30">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-[color:var(--brand-green)] text-white flex items-center justify-center shrink-0">
                    <Bot size={14} />
                  </div>
                )}
                <div
                  className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-[color:var(--brand-green)] text-white rounded-br-sm'
                      : 'bg-white text-[color:var(--ink)] border border-neutral-100 shadow-sm rounded-bl-sm'
                  }`}
                >
                  {m.content}
                </div>
                {m.role === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-[color:var(--brand-orange)] text-white flex items-center justify-center shrink-0">
                    <UserIcon size={14} />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[color:var(--brand-green)] text-white flex items-center justify-center">
                  <Bot size={14} />
                </div>
                <div className="bg-white border border-neutral-100 shadow-sm rounded-2xl px-4 py-3 flex gap-1.5">
                  <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}

            {/* Quick questions (only show at start) */}
            {messages.length <= 1 && !loading && (
              <div className="pt-2 space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--ink-soft)]">
                  Try asking
                </div>
                {QUICK_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    className="w-full text-left px-3 py-2 rounded-xl bg-white border border-[color:var(--brand-green)]/25 text-xs text-[color:var(--ink)] hover:bg-[color:var(--brand-green)] hover:text-white hover:border-[color:var(--brand-green)] transition"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-neutral-100 bg-white flex gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 bg-neutral-100 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[color:var(--brand-green)]/30 border border-transparent focus:border-[color:var(--brand-green)]/40 transition"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="w-11 h-11 rounded-full bg-[color:var(--brand-green)] text-white flex items-center justify-center hover:bg-[color:var(--brand-green-dark)] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={18} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
